import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ChevronLeft, ChevronRight, Globe, Smartphone, TrendingUp, Send, Play, Pause } from "lucide-react";

interface Slide {
  id: number;
  badge: string;
  badgeIcon: React.ReactNode;
  title: string;
  highlight: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  secondaryText: string;
  secondaryLink: string;
  imageUrl: string;
  imageAlt: string;
  features: string[];
}

const slides: Slide[] = [
  {
    id: 1,
    badge: "WEBSITE DEVELOPMENT",
    badgeIcon: <Globe size={14} />,
    title: "High-Performance Web Solutions &",
    highlight: "E-Commerce Platforms",
    description: "Custom React, Next.js, WordPress & Shopify architectures engineered for sub-second speed and SEO dominance.",
    ctaText: "Web Packages",
    ctaLink: "/Pricing",
    secondaryText: "Explore Web Services",
    secondaryLink: "/services/website-development",
    imageUrl: "https://images.pexels.com/photos/577585/pexels-photo-577585.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Website Development & Web Applications",
    features: ["🌐 React & Next.js", "🛒 Shopify & WooCommerce", "⚡ Sub-Second Speed"]
  },
  {
    id: 2,
    badge: "MOBILE APP DEVELOPMENT",
    badgeIcon: <Smartphone size={14} />,
    title: "Native iOS & Android Applications with",
    highlight: "Fluid UI/UX Experience",
    description: "Tailored iOS Swift and Android Kotlin mobile apps engineered for high app store ratings and user retention.",
    ctaText: "App Packages",
    ctaLink: "/Pricing",
    secondaryText: "Explore App Services",
    secondaryLink: "/services/app-development",
    imageUrl: "https://images.pexels.com/photos/1092671/pexels-photo-1092671.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Mobile App Development Solutions",
    features: ["📱 Native iOS & Android", "⚡ Cross-Platform Flutter", "🔒 Secure Cloud API Sync"]
  },
  {
    id: 3,
    badge: "DIGITAL GROWTH MARKETING",
    badgeIcon: <TrendingUp size={14} />,
    title: "Performance Marketing, SEO &",
    highlight: "Multi-Channel Growth",
    description: "Organic search ranking dominance and high-ROI Meta & Google ad campaigns engineered for client acquisition.",
    ctaText: "Marketing Packages",
    ctaLink: "/Pricing",
    secondaryText: "Explore SEO Services",
    secondaryLink: "/services/digital-marketing-seo",
    imageUrl: "https://images.pexels.com/photos/905163/pexels-photo-905163.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Digital Growth & Performance Marketing",
    features: ["🚀 Organic SEO Dominance", "📈 Meta & Google Ads", "🎯 Near-100% Open Rates"]
  },
  {
    id: 4,
    badge: "INSTANT PROJECT ENQUIRY",
    badgeIcon: <Send size={14} />,
    title: "Get Instant Technical Quote &",
    highlight: "Scope Proposal",
    description: "Submit your custom project requirement to receive a formal technical architecture breakdown and milestone proposal.",
    ctaText: "Instant Proposal",
    ctaLink: "/enquirynow",
    secondaryText: "Schedule Meeting",
    secondaryLink: "/Meeting",
    imageUrl: "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Instant Technical Proposal Meeting",
    features: ["⚡ 24h Turnaround", "📋 Detailed Scope Plan", "💼 Dedicated Tech Advisor"]
  }
];

export default function BannerSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoScroll, setIsAutoScroll] = useState<boolean>(false); // BY DEFAULT OFF

  useEffect(() => {
    if (!isAutoScroll) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isAutoScroll]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const slide = slides[currentSlide];

  return (
    <div className="relative w-full min-h-[480px] sm:min-h-[520px] lg:min-h-[580px] bg-gradient-to-br from-slate-50 via-white to-blue-50/40 dark:from-[#0b0f19] dark:via-[#030712] dark:to-[#091124] rounded-2xl md:rounded-3xl border border-slate-200/90 dark:border-white/10 overflow-hidden flex items-center justify-center py-10 px-4 sm:px-8 lg:px-12 mt-0 mb-6 font-body shadow-xl shadow-slate-200/50 dark:shadow-none">
      
      {/* Background Glow Graphics */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/10 dark:bg-blue-600/20 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-violet-500/10 dark:bg-violet-600/20 rounded-full blur-[100px] pointer-events-none" />

      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={(_, info) => {
            if (info.offset.x < -50) nextSlide();
            if (info.offset.x > 50) prevSlide();
          }}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.4 }}
          className="relative z-10 w-full max-w-7xl mx-auto flex flex-col lg:grid lg:grid-cols-12 gap-8 lg:gap-12 items-center text-center lg:text-left cursor-grab active:cursor-grabbing"
        >
          {/* LEFT CONTENT COLUMN (COL 7) */}
          <div className="lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left space-y-4">
            
            {/* TOP BADGE */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-cyan-400 text-xs font-bold font-heading">
              <span className="text-blue-600 dark:text-cyan-400">{slide.badgeIcon}</span>
              <span className="uppercase tracking-wider">{slide.badge}</span>
            </div>

            {/* HERO HEADLINE H1 */}
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.15] font-heading">
              {slide.title}{" "}
              <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-indigo-600 dark:from-cyan-400 dark:via-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                {slide.highlight}
              </span>
            </h1>

            {/* SUBTITLE */}
            <p className="text-xs sm:text-sm md:text-base text-slate-600 dark:text-slate-300 max-w-lg mx-auto lg:mx-0 font-medium leading-relaxed">
              {slide.description}
            </p>

            {/* FEATURE BULLETS */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-2 pt-1 pb-2">
              {slide.features.map((feat, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-700 dark:text-slate-300 shadow-xs"
                >
                  {feat}
                </span>
              ))}
            </div>

            {/* ACTION BUTTONS (CTA) WITH RESPECTIVE PACKAGES */}
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto justify-center lg:justify-start pt-1">
              <Link
                to={slide.ctaLink}
                className="w-full sm:w-auto h-11 sm:h-12 px-6 rounded-xl font-bold text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 active:scale-[0.98] transition-all cursor-pointer"
              >
                <span className="!text-white font-extrabold">{slide.ctaText}</span>
                <ArrowRight size={16} className="!text-white" />
              </Link>

              <Link
                to={slide.secondaryLink}
                className="w-full sm:w-auto h-11 sm:h-12 px-6 rounded-xl font-bold text-xs sm:text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors cursor-pointer"
              >
                <span>{slide.secondaryText}</span>
              </Link>
            </div>

          </div>

          {/* RIGHT VISUAL COLUMN (COL 5) - ANIMATED FREE HIGH-RES IMAGE CARD */}
          <div className="lg:col-span-5 w-full flex justify-center items-center">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="relative w-full max-w-sm sm:max-w-md lg:max-w-none mx-auto overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2 sm:p-3 shadow-2xl group"
            >
              {/* IMAGE PREVIEW WITH OVERLAY GRADIENT */}
              <div className="relative w-full h-52 sm:h-64 md:h-72 rounded-2xl overflow-hidden">
                <img 
                  src={slide.imageUrl} 
                  alt={slide.imageAlt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
                
                {/* FLOATING STATUS BADGE OVER IMAGE */}
                <div className="absolute bottom-3 left-3 right-3 p-3 rounded-xl bg-slate-900/90 backdrop-blur-md border border-white/10 flex items-center justify-between text-white">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[11px] font-extrabold uppercase tracking-wider text-cyan-400">
                      Hickter Verified Tech
                    </span>
                  </div>
                  <Sparkles size={14} className="text-amber-400 animate-spin" />
                </div>
              </div>
            </motion.div>
          </div>

        </motion.div>
      </AnimatePresence>

      {/* CAROUSEL NAVIGATION CHEVRONS (HIDDEN ON MOBILE, FLEX ON SM+) */}
      <button
        onClick={prevSlide}
        aria-label="Previous Slide"
        className="hidden sm:flex absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/90 dark:bg-slate-900/90 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white hover:border-blue-600 dark:hover:border-blue-400 backdrop-blur-md shadow-xl hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer items-center justify-center z-30 group/btn"
      >
        <ChevronLeft size={20} className="text-blue-600 dark:text-cyan-400 group-hover/btn:text-white transition-colors" />
      </button>

      <button
        onClick={nextSlide}
        aria-label="Next Slide"
        className="hidden sm:flex absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/90 dark:bg-slate-900/90 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white hover:border-blue-600 dark:hover:border-blue-400 backdrop-blur-md shadow-xl hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer items-center justify-center z-30 group/btn"
      >
        <ChevronRight size={20} className="text-blue-600 dark:text-cyan-400 group-hover/btn:text-white transition-colors" />
      </button>

      {/* SLIDE INDICATORS & AUTO-SCROLL TOGGLE BUTTON (RAISED PURE WHITE IN LIGHT THEME & SLEEK DARK GLASS IN DARK THEME) */}
      <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2.5 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl px-3.5 py-1.5 rounded-full border border-slate-200/90 dark:border-slate-800 shadow-xl shadow-slate-300/40 dark:shadow-2xl dark:shadow-blue-950/40">
        {/* AUTO-SCROLL PLAY / PAUSE TOGGLE BUTTON (DEFAULT OFF) */}
        <button
          onClick={() => setIsAutoScroll(!isAutoScroll)}
          aria-label={isAutoScroll ? "Pause Auto Scroll" : "Play Auto Scroll"}
          title={isAutoScroll ? "Pause Auto Scroll" : "Start Auto Scroll"}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold transition-all cursor-pointer shadow-xs ${
            isAutoScroll
              ? "bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/40"
              : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700 hover:text-blue-600 dark:hover:text-cyan-400"
          }`}
        >
          {isAutoScroll ? (
            <>
              <Pause size={11} className="text-emerald-600 dark:text-emerald-400" />
              <span className="hidden sm:inline text-[9px] uppercase tracking-wider font-extrabold">Auto ON</span>
            </>
          ) : (
            <>
              <Play size={11} className="text-blue-600 dark:text-cyan-400" />
              <span className="hidden sm:inline text-[9px] uppercase tracking-wider font-extrabold">Auto OFF</span>
            </>
          )}
        </button>

        <div className="w-[1px] h-3.5 bg-slate-300 dark:bg-slate-700 mx-0.5" />

        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              currentSlide === idx
                ? "w-8 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-cyan-400 dark:to-blue-500 shadow-md shadow-blue-500/30 dark:shadow-[0_0_12px_rgba(34,211,238,0.5)]"
                : "w-2.5 bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-600"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
